import React from 'react';
import PushPinIcon from '@mui/icons-material/PushPin';
import { Paper, Skeleton, Grid } from '@mui/material';
import { Box, Typography, Chip } from 'components/mui';
import type { PinnedCase } from 'containers/pages/cantrackDashboard/types';

interface PinnedCasesSectionProps {
  pinnedCases: PinnedCase[];
  loading: boolean;
  onCaseClick: (caseId: number) => void;
}

const STATUS_COLOR_MAP: Record<string, 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
  Open: 'info',
  'In Progress': 'warning',
  Closed: 'success',
  Cancelled: 'error',
  'On Hold': 'warning',
};

const STATUS_ID_TO_TITLE: Record<number, string> = {
  1: 'Open',
  2: 'In Progress',
  3: 'Closed',
  4: 'Cancelled',
  5: 'On Hold',
};

function getStatusLabel(c: PinnedCase): string {
  return c.StatusTitle || STATUS_ID_TO_TITLE[c.StatusId] || 'N/A';
}

function PinnedCasesSection({ pinnedCases, loading, onCaseClick }: PinnedCasesSectionProps) {
  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Pinned Cases
        </Typography>
        <Grid container spacing={2}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} key={i}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="50%" />
                <Skeleton variant="rounded" width={60} height={24} sx={{ mt: 1 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Pinned Cases
      </Typography>
      {pinnedCases.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
          }}
        >
          <PushPinIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Pin cases from the Cases page for quick access here
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {pinnedCases.map((pinned) => (
            <Grid item xs={12} key={pinned.Id}>
              <Paper
                variant="outlined"
                onClick={() => onCaseClick(pinned.Id)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  transition: 'elevation 0.2s',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                <Typography variant="body2" fontWeight={600} noWrap>
                  {pinned.Title}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {pinned.CustomerName}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={getStatusLabel(pinned)}
                    size="small"
                    color={STATUS_COLOR_MAP[getStatusLabel(pinned)] || 'primary'}
                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
}

export default PinnedCasesSection;
